import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { FormError } from '@/components/ui/form-error';
import { PageLoading } from '@/components/ui/loading';
import { Plus, Trash2, Save, Send, Lock } from 'lucide-react';
import type { Project, Shift, LabourTrade, LabourRate, EligibleProjectManager } from '@/lib/types';
import { cn } from '@/lib/utils';
import { requiredText, optionalText, dateFieldRule, headcountRule, hoursRule, rateRule, sameDaySuperRefine, REQUIRED_FIELDS_MESSAGE } from '@/lib/validation-rules';
import { focusFirstInvalidField, handleApiSubmitError } from '@/lib/form-validation';

type SaveIntent = 'DRAFT' | 'SUBMIT';

const schema = z.object({
  projectId: requiredText('Project', 100),
  assignedPmUserId: z.string().optional(),
  startDate: dateFieldRule('Start date'),
  endDate: dateFieldRule('End date'),
  shiftId: z.string().optional(),
  workLocation: optionalText(200),
  activityDescription: optionalText(2000),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).default('normal'),
  notes: optionalText(2000),
  items: z.array(z.object({
    tradeId: requiredText('Trade', 100),
    headcount: headcountRule,
    hours: hoursRule,
    rate: rateRule,
    notes: z.string().optional(),
  })).min(1, 'At least one labour item is required.'),
}).superRefine(sameDaySuperRefine('startDate', 'endDate'));

type FormData = z.infer<typeof schema>;
type ItemFormRow = { tradeId: string; headcount: number; hours: number; rate: number; notes?: string };

export default function RequirementsFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const isEdit = !!id;
  const saveIntentRef = useRef<SaveIntent>('DRAFT');

  const { register, handleSubmit, control, watch, setValue, reset, setError, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { priority: 'normal', items: [{ tradeId: '', headcount: 1, hours: 8, rate: 0 }] },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'items' });

  const { data: projects } = useQuery({ queryKey: ['p'], queryFn: async () => { const r = await api.get<any>('/projects?availability=active'); return (r.data ?? []) as Project[]; } });
  const { data: shifts } = useQuery({ queryKey: ['s'], queryFn: async () => { const r = await api.get<any>('/shifts'); return (r.data ?? []) as Shift[]; } });
  const { data: trades } = useQuery({ queryKey: ['t'], queryFn: async () => { const r = await api.get<any>('/labour-trades'); return (r.data ?? []) as LabourTrade[]; } });
  const { data: rates } = useQuery({ queryKey: ['r'], queryFn: async () => { const r = await api.get<any>('/rates'); return (r.data ?? []) as LabourRate[]; } });

  const { data: eligiblePms, isLoading: pmLoading, isError: pmError, refetch: refetchPms } = useQuery({
    queryKey: ['eligible-pms'],
    queryFn: async () => { const r = await api.get<any>('/requirements/eligible-pms'); return (r.data ?? []) as EligibleProjectManager[]; },
    retry: 0,
  });

  const { data: existing, isLoading: loadingExisting, isError: isErrorExisting } = useQuery({
    queryKey: ['requirement', id],
    queryFn: async () => { const r = await api.get<{ success: boolean; data: any }>(`/requirements/${id}`); return r.data; },
    enabled: isEdit,
  });

  const watchedItems = watch('items') as ItemFormRow[] | undefined;

  useEffect(() => {
    watchedItems?.forEach((item, idx) => {
      if (item?.tradeId && !item?.rate) {
        const rate = rates?.find((r: LabourRate) => r.tradeId === item.tradeId && r.isActive);
        if (rate) {
          setValue(`items.${idx}.rate`, rate.ratePerHour);
        }
      }
    });
  }, [watchedItems, rates, setValue]);

  // ── Same-day dates: auto-sync End Date to Start Date ──────────────
  const watchedStartDate = watch('startDate') as string | undefined;

  useEffect(() => {
    if (watchedStartDate) {
      setValue('endDate', watchedStartDate, { shouldValidate: false, shouldDirty: true });
    } else {
      setValue('endDate', '', { shouldValidate: false, shouldDirty: true });
    }
  }, [watchedStartDate, setValue]);

  // Populate form in edit mode — normalize End Date to Start Date
  useEffect(() => {
    if (existing) {
      const savedStartDate = existing.startDate || '';
      reset({
        projectId: String(existing.projectId ?? existing.project_id ?? ''),
        assignedPmUserId: String(existing.assignedPmUserId ?? existing.assigned_pm_user_id ?? ''),
        startDate: savedStartDate,
        endDate: savedStartDate,
        shiftId: String(existing.shiftId ?? existing.shift_id ?? ''),
        workLocation: existing.workLocation ?? existing.work_location ?? '',
        activityDescription: existing.activityDescription ?? existing.activity_description ?? '',
        priority: existing.priority || 'normal',
        notes: existing.notes || '',
        items: (existing.items || []).map((item: any) => ({
          tradeId: String(item.workerCategoryId ?? item.trade_id ?? item.worker_category_id ?? ''),
          headcount: item.count ?? item.requiredHeadcount ?? item.required_headcount ?? 1,
          hours: item.hours ?? item.plannedHours ?? item.planned_hours ?? 8,
          rate: item.rate ?? item.plannedRate ?? item.planned_rate ?? 0,
        })),
      });
    }
  }, [existing, reset]);

  const mutation = useMutation({
    mutationFn: async (data: FormData) => {
      const intent = saveIntentRef.current;
      const payload = {
        projectId: data.projectId,
        assignedPmUserId: data.assignedPmUserId || undefined,
        startDate: data.startDate,
        endDate: data.endDate,
        shiftId: data.shiftId || undefined,
        workLocation: data.workLocation || undefined,
        activityDescription: data.activityDescription || undefined,
        priority: data.priority,
        notes: data.notes || undefined,
        intent,
        labourItems: data.items.map(item => ({
          tradeId: item.tradeId,
          headcount: item.headcount,
          hours: item.hours,
          rate: item.rate,
        })),
      };
      if (isEdit) {
        return api.put(`/requirements/${id}`, payload);
      }
      return api.post('/requirements', payload);
    },
    onSuccess: () => {
      const intent = saveIntentRef.current;
      addToast(
        intent === 'DRAFT' ? 'Requirement saved as draft.' : 'Requirement submitted to Manager for approval.',
        intent === 'DRAFT' ? 'info' : 'success'
      );
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['report-project-summary'] });
      queryClient.invalidateQueries({ queryKey: ['report-category-detail'] });
      queryClient.invalidateQueries({ queryKey: ['report-budget-actual'] });
      queryClient.invalidateQueries({ queryKey: ['report-multi-summary'] });
      queryClient.invalidateQueries({ queryKey: ['requirement', id] });
      navigate('/requirements');
    },
    onError: (err: any) => {
      handleApiSubmitError(err, { addToast, setError: (name, error) => setError(name as any, error) });
    },
    onSettled: () => {
      saveIntentRef.current = 'DRAFT';
    },
  });

  const onInvalid = (errs: FieldErrors<FormData>) => {
    focusFirstInvalidField(errs);
    addToast(REQUIRED_FIELDS_MESSAGE, 'error');
  };

  const onSubmit = (data: FormData) => {
    if (saveIntentRef.current === 'SUBMIT' && !data.assignedPmUserId) {
      setError('assignedPmUserId', { message: 'A Project Manager must be selected before submitting.' });
      addToast('A Project Manager must be selected before submitting.', 'error');
      return;
    }
    mutation.mutate(data);
  };

  const isPending = mutation.isPending;

  const savedPmId = watch('assignedPmUserId') || '';
  const savedPmName = existing?.assignedPmName ?? existing?.assigned_pm_name ?? null;
  const savedPmEmail = existing?.assignedPmEmail ?? existing?.assigned_pm_email ?? null;
  const pmOptionsLoaded = !pmLoading && !pmError && !!eligiblePms;
  const savedPmNotEligible = pmOptionsLoaded && !!savedPmId && !eligiblePms!.some((p) => p.id === savedPmId);
  const pmOptions = [
    ...(eligiblePms ?? []).map((p) => ({ value: p.id, label: `${p.name} — ${p.email}` })),
    ...(savedPmNotEligible && savedPmName ? [{ value: savedPmId, label: `${savedPmName} — ${savedPmEmail ?? ''} (previously assigned)` }] : []),
  ];

  if (isEdit && loadingExisting) return <PageLoading />;
  if (isEdit && isErrorExisting) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <h2 className="text-xl font-bold text-primary">Not Found</h2>
        <p className="text-secondary mt-2">This requirement could not be found or you do not have access.</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate('/requirements')}>Back to Requirements</Button>
      </div>
    );
  }

  if (!projects || !trades) return <PageLoading />;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <h1 className="text-2xl font-heading font-bold text-primary">{isEdit ? 'Edit Requirement' : 'New Requirement'}</h1>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Project *</Label>
              <Select value={watch('projectId') || ''} onValueChange={v => setValue('projectId', v)} disabled={isEdit}>
                <SelectTrigger data-field-name="projectId" aria-invalid={errors.projectId ? true : undefined} aria-describedby={errors.projectId ? 'projectId-error' : undefined}><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p: Project) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <FormError id="projectId-error" message={errors.projectId?.message} />
            </div>
            <div>
              <Label>Project Manager *</Label>
              {pmLoading ? (
                <p className="text-sm text-secondary h-10 flex items-center">Loading Project Managers...</p>
              ) : pmError ? (
                <div className="space-y-2">
                  <p className="text-sm text-error">Unable to load eligible Project Managers.</p>
                  <Button type="button" variant="outline" size="sm" onClick={() => refetchPms()}>Retry</Button>
                </div>
              ) : (
                <SearchableSelect
                  value={watch('assignedPmUserId') || ''}
                  onValueChange={v => setValue('assignedPmUserId', v)}
                  options={pmOptions}
                  placeholder="Select Project Manager"
                  searchPlaceholder="Search by name or email..."
                  emptyMessage="No eligible Project Managers"
                  data-field-name="assignedPmUserId"
                  aria-invalid={errors.assignedPmUserId ? true : undefined}
                  aria-describedby={errors.assignedPmUserId ? 'assignedPmUserId-error' : undefined}
                />
              )}
              {savedPmNotEligible && (
                <p className="text-xs text-error mt-1">The previously assigned Project Manager is no longer active or eligible. Select a new one before submitting.</p>
              )}
              <FormError id="assignedPmUserId-error" message={errors.assignedPmUserId?.message} />
            </div>
            <div>
              <Label>Start Date *</Label>
              <Input type="date" {...register('startDate')} aria-invalid={errors.startDate ? true : undefined} aria-describedby={errors.startDate ? 'startDate-error' : undefined} className={cn(errors.startDate && 'border-error')} />
              <FormError id="startDate-error" message={errors.startDate?.message} />
            </div>
            <div>
              <Label>End Date *</Label>
              <div className="relative">
                <Input type="date" value={watch('endDate') || ''} readOnly tabIndex={-1} aria-invalid={errors.endDate ? true : undefined} aria-describedby={errors.endDate ? 'endDate-error endDate-locked' : 'endDate-locked'} className={cn('bg-surface-secondary cursor-not-allowed pr-9', errors.endDate && 'border-error')} />
                <Lock size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
              </div>
              <p id="endDate-locked" className="text-xs text-muted mt-1">End Date is automatically the same as Start Date.</p>
              <FormError id="endDate-error" message={errors.endDate?.message} />
            </div>
            <div>
              <Label>Shift</Label>
              <Select value={watch('shiftId') || ''} onValueChange={v => setValue('shiftId', v)}>
                <SelectTrigger data-field-name="shiftId"><SelectValue placeholder="Select shift" /></SelectTrigger>
                <SelectContent>
                  {shifts?.map((s: Shift) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Work Location</Label><Input {...register('workLocation')} placeholder="e.g. Floor 5 North" /></div>
            <div className="md:col-span-2">
              <Label>Activity Description</Label>
              <Textarea {...register('activityDescription')} placeholder="Describe the work..." />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={watch('priority') || 'normal'} onValueChange={v => setValue('priority', v as any)}>
                <SelectTrigger data-field-name="priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Input {...register('notes')} /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Labour Items</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={() => append({ tradeId: '', headcount: 1, hours: 8, rate: 0 })}>
              <Plus size={14} /> Add Item
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {errors.items && !Array.isArray(errors.items) && <FormError message={errors.items.message} />}
            {fields.map((field, idx) => (
              <div key={field.id} className="grid grid-cols-12 gap-3 items-end border rounded-lg p-3">
                <div className="col-span-4">
                  <Label>Trade *</Label>
                  <Select value={watch(`items.${idx}.tradeId`) || ''} onValueChange={v => setValue(`items.${idx}.tradeId`, v)}>
                    <SelectTrigger data-field-name={`items.${idx}.tradeId`} aria-invalid={errors.items?.[idx]?.tradeId ? true : undefined} aria-describedby={errors.items?.[idx]?.tradeId ? `items-${idx}-tradeId-error` : undefined}><SelectValue placeholder="Select trade" /></SelectTrigger>
                    <SelectContent>
                      {trades?.map((t: LabourTrade) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormError id={`items-${idx}-tradeId-error`} message={errors.items?.[idx]?.tradeId?.message} />
                </div>
                <div className="col-span-2">
                  <Label>Headcount *</Label>
                  <Input type="number" {...register(`items.${idx}.headcount`)} min={1} aria-invalid={errors.items?.[idx]?.headcount ? true : undefined} aria-describedby={errors.items?.[idx]?.headcount ? `items-${idx}-headcount-error` : undefined} className={cn(errors.items?.[idx]?.headcount && 'border-error')} />
                  <FormError id={`items-${idx}-headcount-error`} message={errors.items?.[idx]?.headcount?.message} />
                </div>
                <div className="col-span-2">
                  <Label>Hours *</Label>
                  <Input type="number" {...register(`items.${idx}.hours`)} min={1} aria-invalid={errors.items?.[idx]?.hours ? true : undefined} aria-describedby={errors.items?.[idx]?.hours ? `items-${idx}-hours-error` : undefined} className={cn(errors.items?.[idx]?.hours && 'border-error')} />
                  <FormError id={`items-${idx}-hours-error`} message={errors.items?.[idx]?.hours?.message} />
                </div>
                <div className="col-span-2">
                  <Label>Rate (₹/hr)</Label>
                  <Input type="number" step="0.01" {...register(`items.${idx}.rate`)} aria-invalid={errors.items?.[idx]?.rate ? true : undefined} aria-describedby={errors.items?.[idx]?.rate ? `items-${idx}-rate-error` : undefined} className={cn(errors.items?.[idx]?.rate && 'border-error')} />
                  <FormError id={`items-${idx}-rate-error`} message={errors.items?.[idx]?.rate?.message} />
                </div>
                <div className="col-span-1">
                  <p className="text-xs text-secondary mb-1">Amount</p>
                  <p className="text-sm font-medium">
                    ₹{((watchedItems?.[idx]?.hours || 0) * (watchedItems?.[idx]?.rate || 0) * (watchedItems?.[idx]?.headcount || 0)).toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="col-span-1">
                  {fields.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => remove(idx)}>
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" disabled={isPending} onClick={() => navigate('/requirements')}>Cancel</Button>
          <Button type="submit" variant="secondary" disabled={isPending} loading={isPending && saveIntentRef.current === 'DRAFT'} onClick={() => { saveIntentRef.current = 'DRAFT'; }}>
            <Save size={16} /> Save Draft
          </Button>
          <Button type="submit" disabled={isPending} loading={isPending && saveIntentRef.current === 'SUBMIT'} onClick={() => { saveIntentRef.current = 'SUBMIT'; }}>
            <Send size={16} /> Submit
          </Button>
        </div>
      </form>
    </div>
  );
}
